<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router';
import type { Severity } from '../../types/severity';
import type { Size } from '../../types/size';
import type { Target } from '../../types/target';
import { Tooltip } from 'floating-vue';
import { resolveComponent } from 'vue';
import Icon from '../Icon.vue';

const props = withDefaults(defineProps<{
	label?: string;
	icon?: string;
	severity?: Severity | 'light' | 'dark';
	size?: Extract<Size, 'sm' | 'md'>;
	to?: RouteLocationRaw;
	external?: boolean;
	target?: Target;
	isActive?: boolean;
	disabled?: boolean;
	info?: string;
	loading?: boolean;
}>(), {
	severity: 'light',
	size: 'md',
});

const NuxtLink = resolveComponent('NuxtLink');
</script>

<template>
	<component
		:class="props.info ? undefined : 'dropdown-item-wrapper'"
		:placement="props.info ? 'right-center' : undefined"
		:is="props.info ? Tooltip : 'div'"
	>
		<component
			class="dropdown-item"
			:class="{
				[`dropdown-item--${props.severity}`]: props.severity,
				[`dropdown-item--${props.size}`]: props.size,
				'dropdown-item--is-active': props.isActive,
				'dropdown-item--disabled': props.disabled,
			}"
			:to="props.to"
			:type="props.to ? undefined : 'button'"
			:external="props.external"
			:target="props.target"
			:disabled="props.disabled"
			:is="props.to ? NuxtLink : 'button'"
		>
			<span class="dropdown-item__content">
				<span class="dropdown-item__icon" v-if="props.icon || props.loading">
					<HmSpinner size="xs" v-if="props.loading" />
					<Icon :name="props.icon" v-else-if="props.icon" />
				</span>
				<span v-if="props.label">{{ props.label }}</span>
				<slot v-else-if="$slots.default" />
			</span>
			<span class="dropdown-item__info" v-if="props.info">
				<Icon name="info" />
			</span>
		</component>

		<template #popper>
			{{ props.info }}
		</template>
	</component>
</template>

<style scoped lang="scss">
@layer components
{
	.dropdown-item-wrapper
	{
		@include flex;
	}
	.dropdown-item
	{
		flex-grow:1;
		@include flex(null, center, var(--spacing-xs));
		padding:var(--dropdown-py) var(--spacing-lg);
		transition:var(--transition-default);

		&__content
		{
			flex-grow:1;
			@include flex(null, center, var(--spacing-xs));
			color:var(--dropdown-c);
			font-size:var(--dropdown-fs);
			line-height:var(--dropdown-icon);
			font-weight:500;
		}
		&__icon
		{
			@include flex;

			&:deep(.icon)
			{
				@include size(var(--dropdown-icon));

				svg path
				{
					vector-effect:non-scaling-stroke;
				}
			}
		}
		&__info
		{
			@include flex;

			&:deep(.icon)
			{
				@include svg(16);
			}
		}

		&:not(.dropdown-item--disabled)
		{
			@include hover
			{
				background-color:var(--dropdown-bg);
			}
		}

		&--light
		{
			--dropdown-c:var(--fill-c-light);
			--dropdown-bg:var(--fill-bg-light);
		}
		&--dark
		{
			--dropdown-c:var(--fill-c-dark);
			--dropdown-bg:var(--fill-bg-dark);
		}
		&--success
		{
			--dropdown-c:var(--fill-c-success);
			--dropdown-bg:var(--fill-bg-success);
		}
		&--warning
		{
			--dropdown-c:var(--fill-c-warning);
			--dropdown-bg:var(--fill-bg-warning);
		}
		&--error
		{
			--dropdown-c:var(--fill-c-error);
			--dropdown-bg:var(--fill-bg-error);
		}
		&--info
		{
			--dropdown-c:var(--fill-c-info);
			--dropdown-bg:var(--fill-bg-info);
		}

		&--sm
		{
			--dropdown-py:var(--spacing-xs);
			--dropdown-icon:1.6rem;
			--dropdown-fs:var(--text-caption);
		}
		&--md
		{
			--dropdown-py:var(--spacing-sm);
			--dropdown-icon:2rem;
			--dropdown-fs:var(--text-body-sm);
		}

		&--is-active
		{
			color:var(--c-title);
			background-color:var(--border-secondary);

			&:not(.dropdown-item--disabled)
			{
				@include hover
				{
					background-color:var(--basic-400);
				}
			}
		}
		&--disabled
		{
			cursor:not-allowed;

			.dropdown-item__content
			{
				opacity:0.36;
			}
		}
	}
}
</style>
